import { type TFunction } from "i18next";
import {
  type ConditionEntry,
  type ConditionLeaf,
  isConditionGroup,
} from "@/components/ConditionBuilder";
import { type ResourcePolicyDraft } from "./types";

/** Returns true when a leaf has an attribute selected (a "started" condition). */
const isStartedLeaf = (entry: ConditionEntry): boolean =>
  !isConditionGroup(entry) && !!(entry as ConditionLeaf).attribute?.id;

/** Counts started condition leaves across a condition tree (nested included). */
export function countResourceConditions(
  conditions: ConditionEntry[] | undefined,
): number {
  if (!conditions?.length) return 0;
  return conditions.reduce((total, entry) => {
    if (isConditionGroup(entry)) {
      return total + countResourceConditions(entry.conditions);
    }
    return total + (isStartedLeaf(entry) ? 1 : 0);
  }, 0);
}

/**
 * One-line human-readable summary of a resource policy for the Review step.
 * e.g. "Metadata-based · 2 conditions" or "Specific: /Board/minutes.pdf".
 */
export function summarizeResourcePolicy(
  policy: ResourcePolicyDraft,
  t: TFunction,
): string {
  if (policy.scope === "specific") {
    return t("mcpRegistry.reviewAndSave.resourceScopeSpecific", {
      path: policy.specificPath?.trim() || "…",
    });
  }
  const count = countResourceConditions(policy.conditions);
  const conditionText = t("mcpRegistry.reviewAndSave.resourceConditionCount", {
    count,
  });
  return `${t("mcpRegistry.reviewAndSave.resourceScopeMetadata")} · ${conditionText}`;
}
